import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  AgreementContratStatus,
  Agreements,
  BuySpecs,
  commodityOptions,
  CurrenciesConversions,
  Invoices,
  Items,
  Samples,
  SampleShipmentRequests,
  SampleShipments,
  SelectOptions,
  ShipmentFulfillments,
  StoreDataTypeKey,
} from '@etoh/database/core';
import { UserService } from 'apps/desktop/src/app/user.service';
import { groupByMultiple } from 'apps/desktop/src/app/utilities/groupByMultiple';
import copy from 'fast-copy';
import { flatten, groupBy, reverse, sortBy, sum, takeRight } from 'lodash';

import { isSameYear, parseISO } from 'date-fns';

import { combineLatest, Observable, of, Subject } from 'rxjs';
import { map, shareReplay, startWith, take, takeUntil } from 'rxjs/operators';
import { AgreementsService } from '../../../agreements/agreements.service';
import { ItemsService } from '../../../items/items.service';
import { OfficesService } from '../../../offices/offices.service';
import { getUom } from '../../../shared/forms/uom';
import { StoreService } from '../../../store.service';
import { UsersService } from '../../../users.service';
import { StatsSection } from '../../dashboard.interfaces';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';

import { CommonModule } from '@angular/common';
import { FlexLayoutModule } from '@angular/flex-layout';
import { RouterModule } from '@angular/router';
import { Users } from '@etoh/database/angular';
import { convertCurrencyToUSD } from 'apps/desktop/src/app/currenciesConversions/pure';
import {
  formatCurrency,
  formatNumber,
} from 'apps/desktop/src/app/locales/locales';
import { isAgreementLost } from '../../../buy-specs/buy-specs.pure';
import { StatsDisplaySectionsComponent } from '../../components/stats-display-sections/stats-display-sections.component';

interface ItemsExtended extends Items {
  agreementsIds?: number[];
}

interface InvoiceGroupByCurrency {
  currency: string;
  count: number;
  sum: number;
}

const LAST_LIMIT = 10;

@Component({
  selector: 'etoh-more',
  standalone: true,
  imports: [
    CommonModule,
    NzFormModule,
    FlexLayoutModule,
    NzSelectModule,
    FormsModule,
    NzSpinModule,
    ReactiveFormsModule,
    StatsDisplaySectionsComponent,
    RouterModule,
  ],
  template: `
    <section>
      <div fxLayout="row" fxLayoutAlign="center center">
        <div>
          <form nz-form nzLayout="vertical">
            <nz-form-item>
              <nz-form-label>Commodity type</nz-form-label>
              <nz-select
                [formControl]="selectedCommodity"
                nzShowSearch
                nzPlaceHolder="Select the commodity type"
                name="selectedCommodity"
              >
                @for (option of commodityOptions; track option.value) {
                <nz-option
                  [nzLabel]="option.label"
                  [nzValue]="option.value"
                ></nz-option>
                }
              </nz-select>
            </nz-form-item>
          </form>
        </div>
      </div>

      @if (usersOptions$ | async; as usersOptions) {
      <div fxLayout="row" fxLayoutAlign="space-around center">
        <div>
          <form nz-form nzLayout="vertical">
            <nz-form-item>
              <nz-form-label>My figure</nz-form-label>
              <nz-select
                [formControl]="selectedUserId"
                nzShowSearch
                nzPlaceHolder="Select the user"
                name="selectedUser"
              >
                @for (option of usersOptions; track option.value) {
                <nz-option
                  [nzLabel]="option.label"
                  [nzValue]="option.value"
                ></nz-option>
                }
              </nz-select>
            </nz-form-item>
          </form>
        </div>
        <div>
          <form nz-form nzLayout="vertical">
            <nz-form-item>
              <nz-form-label>My office</nz-form-label>
              <nz-select
                [formControl]="selectedOffice"
                name="selectedOffice"
                nzPlaceHolder="Select an office"
                nzShowSearch
              >
                @for (option of offices$ | async; track option.value) {
                <nz-option
                  [nzLabel]="option.label"
                  [nzValue]="option.value"
                ></nz-option>
                }
              </nz-select>
            </nz-form-item>
          </form>
        </div>
      </div>
      }
    </section>

    <div nz-row [nzGutter]="[16, 16]">
      @for (role of roles; track $index) {
      <div nz-col class="gutter-row" [nzSpan]="6">
        <div class="text-center">
          <div class="mat-h3">
            {{ role }}
          </div>
        </div>
      </div>
      }
    </div>

    @defer (on viewport) { @if (earnings$ | async; as earnings) {
    <section>
      <div fxLayout="row" fxLayoutGap="8px">
        <div fxFlex>
          <etoh-stats-display-sections [sections]="earnings.user.statsText">
          </etoh-stats-display-sections>
        </div>
        <div fxFlex>
          <etoh-stats-display-sections [sections]="earnings.office.statsText">
          </etoh-stats-display-sections>
        </div>
      </div>
    </section>
    } @else {
    <nz-spin></nz-spin>
    } } @placeholder {
    <nz-spin></nz-spin>
    } @defer (on viewport) {
    <section>
      <br />
      <br />
      <br />

      <div class="mat-h2">BROKERAGE SYSTEM SNAPSHOT</div>

      <div
        fxLayout="row wrap"
        fxLayout.xs="column"
        fxLayoutAlign="space-around center"
      >
        <div>
          <form nz-form nzLayout="vertical">
            <nz-form-item>
              <nz-form-label>Commodity type</nz-form-label>
              <nz-select
                [formControl]="commodityForSnapshot"
                nzShowSearch
                nzPlaceHolder="Select the commodity type"
                name="selectedUser"
              >
                @for (option of commodityOptions; track option) {
                <nz-option
                  [nzLabel]="option.label"
                  [nzValue]="option.value"
                ></nz-option>
                }
              </nz-select>
            </nz-form-item>
          </form>
        </div>

        <div>
          <form nz-form nzLayout="vertical">
            <nz-form-item>
              <nz-form-label>My office</nz-form-label>
              <nz-select
                [formControl]="selectedOfficeForSnapshot"
                name="selectedOffice"
                nzPlaceHolder="Select an office"
                nzShowSearch
              >
                @for (option of offices$ | async; track option) {
                <nz-option
                  [nzLabel]="option.label"
                  [nzValue]="option.value"
                ></nz-option>
                }
              </nz-select>
            </nz-form-item>
          </form>
        </div>
      </div>

      @if (earningsBuySpecs$ | async; as earningsBuySpecs) {
      <div>
        <etoh-stats-display-sections [sections]="earningsBuySpecs" [nzSpan]="8">
        </etoh-stats-display-sections>
      </div>
      } @else {
      <nz-spin></nz-spin>
      }
    </section>
    } @placeholder {
    <nz-spin></nz-spin>
    } @defer (on viewport) {
    <section>
      <br />
      <br />
      <br />

      <!--

    <div class="mat-h2">QUICK VIEW</div>

    <div
      fxLayout="row wrap"
      fxLayout.xs="column"
      fxLayoutAlign="space-around center"
    >
      <div>
        <form nz-form nzLayout="vertical">
          <nz-form-item>
            <nz-form-label>Commodity type</nz-form-label>
            <nz-select
              [formControl]="commodityForLast"
              nzShowSearch
              nzPlaceHolder="Select the commodity type"
              name="selectedUser"
              nzAllowClear
            >
              @for (option of commodityOptions; track option) {
              <nz-option
                [nzLabel]="option.label"
                [nzValue]="option.value"
              ></nz-option>
              }
            </nz-select>
          </nz-form-item>
        </form>
      </div>

      <div>
        <form nz-form nzLayout="vertical">
          <nz-form-item>
            <nz-form-label>My office</nz-form-label>
            <nz-select
              [formControl]="officeForLast"
              name="officeForLast"
              nzPlaceHolder="Select an office"
              nzShowSearch
            >
              @for (option of offices$ | async; track option) {
              <nz-option
                [nzLabel]="option.label"
                [nzValue]="option.value"
              ></nz-option>
              }
            </nz-select>
          </nz-form-item>
        </form>
      </div>
    </div>

    @defer () { @if (quickViews$ | async; as quickViews) {
    <div nz-row [nzGutter]="[16, 16]">
      @for (view of quickViews; track view) {
      <div
        nz-col
        class="gutter-row"
        [nzLg]="12"
        [nzMd]="24"
        [nzSm]="24"
        [nzXs]="24"
      >
        <div class="card mat-padding" style="height: 100%">
          <div>
            <div class="mat-h3">{{ view.title }}</div>
          </div>
              <etoh-list
                [height]="'566px'"
                [data$]="view.value"
                [dataType]="view.dataType"
              ></etoh-list>
            </div>
          </div>
        }
      </div>
    } @else {
      <nz-spin></nz-spin>
    } }
  -->
    </section>
    } @placeholder {
    <nz-spin></nz-spin>
    }

    <!--
  <section>
    <div class="mat-h2">GLOBAL STATISTICS</div>

    <etoh-stats-display [sections]="sectionEarningsDataList">
    </etoh-stats-display>
  </section>

  <section>
    <div class="mat-h2">INVOICES</div>
  </section>
  -->
  `,
  styles: `
nz-divider {
  color: #333;
  font-weight: normal;
}

.inner-box {
  background: #0092ff;
  padding: 8px 0;
}

nz-select {
  width: 100%;
}

img {
  width: 100%;
}


form {
  max-width: 100%;
  width: 256px;
}


.loading {
  height: 100vh;
}

  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MoreComponent implements OnInit, OnDestroy {
  roles = ['This Month', 'This Year', 'This Month', 'This Year'];

  commodityOptions = [...commodityOptions, { label: 'All', value: 'all' }];

  monthDates = this._getDatesPeriod('month');
  yearDates = this._getDatesPeriod('year');

  usersOptions$: Observable<SelectOptions[]>;

  offices$: Observable<SelectOptions[]>;

  quickViews$: Observable<
    {
      title: string;
      value: Observable<any[]>;
      dataType: StoreDataTypeKey;
    }[]
  >;

  buySpecs$: Observable<BuySpecs[]>;
  items$: Observable<Items[]>;

  private storeService = inject(StoreService);
  private userService = inject(UserService);
  private officeService = inject(OfficesService);
  private usersService = inject(UsersService);
  private agreementsService = inject(AgreementsService);
  private itemsService = inject(ItemsService);
  private cd = inject(ChangeDetectorRef);
  private zone = inject(NgZone);

  selectedCommodity = new FormControl(
    this.userService.getJsonField(
      'savedFormFields',
      'dashboardSummaryCommodityType'
    ) ?? 'all'
  );
  selectedUserId = new FormControl(null);
  selectedOffice = new FormControl(null);

  commodityForSnapshot = new FormControl(
    this.userService.getJsonField(
      'savedFormFields',
      'dashboardSnapshotCommodityType'
    ) ?? 'all'
  );
  selectedOfficeForSnapshot = new FormControl(null);

  commodityForLast = new FormControl(
    this.userService.getJsonField(
      'savedFormFields',
      'dashboardQuickviewCommodityType'
    )
  );
  officeForLast = new FormControl(null);

  earnings$: Observable<{
    user: {
      statsText: StatsSection[];
    };
    office: {
      statsText: StatsSection[];
    };
  }>;

  earningsBuySpecs$: Observable<StatsSection[]>;

  private _destroy$ = new Subject();

  ngOnInit(): void {
    this.selectedCommodity.valueChanges
      .pipe(
        takeUntil(this._destroy$),
        map((value) => (value === 'all' ? null : value))
      )
      .subscribe((value) => {
        this.userService.updateJsonbField(
          'savedFormFields',
          'dashboardSummaryCommodityType',
          value
        );
      });
    this.commodityForSnapshot.valueChanges
      .pipe(
        takeUntil(this._destroy$),
        map((value) => (value === 'all' ? null : value))
      )
      .subscribe((value) => {
        this.userService.updateJsonbField(
          'savedFormFields',
          'dashboardSnapshotCommodityType',
          value
        );
      });
    this.commodityForLast.valueChanges
      .pipe(takeUntil(this._destroy$))
      .subscribe((value) => {
        this.userService.updateJsonbField(
          'savedFormFields',
          'dashboardQuickviewCommodityType',
          value
        );
      });

    this.usersOptions$ = this.usersService.getBrokersForOptions$();
    this.selectedUserId.patchValue(this.userService.userId$.value as any);

    this.offices$ = this.officeService.getOfficesForOptions$();
    this.selectedOffice.patchValue(this.userService.userOfficeId$.value as any);
    this.selectedOfficeForSnapshot.patchValue(
      this.userService.userOfficeId$.value as any
    );

    this.officeForLast.patchValue(this.userService.userOfficeId$.value as any);

    this.quickViews$ = combineLatest([
      this.storeService.store.buySpecs.data$,
      this.storeService.store.items.data$,
      this.storeService.getStoreByKeys$<Items>('items'),
      this.storeService.store.sampleShipmentRequests.data$,
      this.storeService.store.samples.data$,
      this.storeService.store.shipmentFulfillments.data$,
      this.storeService.store.agreements.data$,
      this.storeService.getStoreByKeys$<Agreements>('agreements'),
      this.commodityForLast.valueChanges.pipe(
        startWith(this.commodityForLast.value)
      ),
      this.officeForLast.valueChanges.pipe(startWith(this.officeForLast.value)),
    ]).pipe(
      map(
        ([
          buySpecs,
          items,
          itemsByKeys,
          sampleShipmentRequests,
          samples,
          shipmentFulfillments,
          agreements,
          agreementsByKeys,
          commodity,
          office,
        ]) => {
          return this.zone.runOutsideAngular(() => {
            const l = (data: any[]) => {
              const filteredData = data.filter((b) => !b.archivedAt);
              return reverse(
                takeRight(sortBy(filteredData, 'createdAt'), LAST_LIMIT)
              );
            };

            let extendedSampleShipmentRequests: SampleShipmentRequests[] = copy(
              sampleShipmentRequests
            );
            extendedSampleShipmentRequests = extendedSampleShipmentRequests.map(
              (s) => {
                return {
                  ...s,
                  item: itemsByKeys[s.itemId],
                };
              }
            );

            let shipmentFulfillmentsExtended: ShipmentFulfillments[] =
              copy(shipmentFulfillments);
            shipmentFulfillmentsExtended = shipmentFulfillmentsExtended.map(
              (a) => {
                return {
                  ...a,
                  agreement: agreementsByKeys[a.agreementId],
                };
              }
            );

            return [
              {
                title: 'New BuySpecs',
                dataType: 'buySpecs',
                value: of(
                  l(
                    buySpecs.filter(
                      (b: BuySpecs) =>
                        b.officeId === office &&
                        (!commodity || b.commodityType === commodity)
                    )
                  )
                ),
              },
              {
                title: 'New Items',
                dataType: 'items',
                value: of(
                  l(
                    items.filter(
                      (b: Items) =>
                        b.officeId === office &&
                        (!commodity || b.commodityType === commodity)
                    )
                  )
                ),
              },
              {
                title: 'New Sample Shipment Requests',
                dataType: 'sampleShipmentRequests',
                value: of(
                  l(
                    extendedSampleShipmentRequests.filter(
                      (b: SampleShipmentRequests) =>
                        b?.item?.officeId === office &&
                        (!commodity || b?.item?.commodityType === commodity)
                    )
                  )
                ),
              },
              {
                title: 'Lastest Samples received by Buyer',
                dataType: 'samples',
                value: of(
                  l(
                    samples.filter(
                      (b: Samples) =>
                        b.officeId === office &&
                        (!commodity || b.commodityType === commodity)
                    )
                  )
                ),
              },

              {
                title: 'Lastest Approved S&P',
                dataType: 'agreements',
                value: of(
                  l(
                    agreements.filter(
                      (a: Agreements) =>
                        a.contractStatus === 'approvedByParties' &&
                        a.contractOfficeId === office &&
                        (!commodity || a.commodityType === commodity)
                    )
                  )
                ),
              },

              {
                title: 'Lastest Item Fulfillments',
                dataType: 'shipmentFulfillments',
                value: of(
                  l(
                    shipmentFulfillmentsExtended.filter((s) => {
                      return (
                        s?.agreement?.contractOfficeId === office &&
                        (!commodity ||
                          s?.agreement?.commodityType === commodity)
                      );
                    })
                  )
                ),
              },
            ];
          });
        }
      )
    );

    this.buySpecs$ = this.storeService.store.buySpecs.data$.pipe(
      map((buySpecs) => {
        return takeRight(sortBy<BuySpecs>(buySpecs, 'createdAt'), LAST_LIMIT);
      })
    );

    this.items$ = this.storeService.store.buySpecs.data$.pipe(
      map((items) => {
        return takeRight(sortBy<Items>(items, 'createdAt'), LAST_LIMIT);
      })
    );

    this.userService.user$
      .pipe(
        map((user) => !(user?.isManager || user?.isAdmin)),
        take(1)
      )
      .subscribe((res) => {
        if (res) {
          this.selectedOffice.disable();
          this.selectedUserId.disable();
          this.selectedOfficeForSnapshot.disable();

          this.officeForLast.disable();
        }
      });

    const itemExtended$: Observable<ItemsExtended[]> = combineLatest([
      this.storeService.store.items.data$,
      this.storeService.store.agreements.data$,
    ]).pipe(
      map(([items, agreements]) => {
        return this.zone.runOutsideAngular(() => {
          const agreementsGroupByItemsId = groupByMultiple(
            agreements,
            'itemsId'
          );
          return items.map((data: Items) => ({
            ...data,
            agreementsIds: agreementsGroupByItemsId[data.id]?.map(
              (agreement) => agreement.id
            ),
          }));
        });
      })
    );

    this.earnings$ = combineLatest([
      this.storeService.store.usersBonus.data$,
      this.storeService.store.usersCosts.data$,
      this.storeService.getStoreByKeys$<Users>('users'),
      this.storeService.store.invoices.data$,
      this.storeService.store.agreements.data$,
      this.storeService.getStoreByKeys$<Agreements>('agreements'),
      this.storeService.getStoreByKeys$<ShipmentFulfillments>(
        'shipmentFulfillments'
      ),
      itemExtended$,
      this.selectedCommodity.valueChanges.pipe(
        startWith(this.selectedCommodity.value),
        map((value) => (value === 'all' ? null : value))
      ),
      this.selectedUserId.valueChanges.pipe(
        startWith(this.selectedUserId.value)
      ),
      this.selectedOffice.valueChanges.pipe(
        startWith(this.selectedOffice.value)
      ),
      this.storeService.store.currenciesConversions.data$,
    ]).pipe(
      map(
        ([
          usersBonus,
          usersCosts,
          usersByKey,
          invoices,
          agreements,
          agreementsByKeys,
          shipmentFulfillmentsByKeys,
          itemsExtended,
          selectedCommodity,
          selectedUserId,
          selectedOffice,
          currenciesConversions,
        ]) => {
          return this.zone.runOutsideAngular(() => {
            let extendedInvoices: Invoices[] = copy(invoices) as Invoices[];

            extendedInvoices = extendedInvoices.filter(
              (invoice: Invoices) => invoice.status !== 'void'
            );

            extendedInvoices = extendedInvoices
              .map((i) =>
                this.storeService.jointObject(
                  i,
                  'agreementId',
                  'agreement',
                  agreementsByKeys
                )
              )
              .map((i) =>
                this.storeService.jointObject<Invoices>(
                  i,
                  'shipmentFulfillmentId',
                  'shipmentFulfillment',
                  shipmentFulfillmentsByKeys
                )
              );

            const startDateMonth = this.monthDates[0];
            const endDateMonth = this.monthDates[1];

            const startDateYear = this.yearDates[0];
            const endDateYear = this.yearDates[1];

            const invoicesMonth = this.getEntitiesFromDateToDate(
              extendedInvoices,
              startDateMonth,
              endDateMonth,
              'createdAt'
            );

            const invoicesYear: Invoices[] = extendedInvoices.filter((i) =>
              isSameYear(parseISO(i.createdAt), parseISO(startDateYear))
            );

            const agreementsMonth = this.getEntitiesFromDateToDate<Agreements>(
              agreements,
              startDateMonth,
              endDateMonth,
              'startDate'
            );

            const agreementsYear = this.getEntitiesFromDateToDate<Agreements>(
              agreements,
              startDateYear,
              endDateYear,
              'startDate'
            );

            const pendingStatus: AgreementContratStatus[] = [
              'draft',
              'toBeSent',
              'pendingApproval',
            ];

            const itemsMonth = this.getEntitiesFromDateToDate<ItemsExtended>(
              itemsExtended,
              startDateMonth,
              endDateMonth,
              'createdAt'
            );

            const itemsYear = this.getEntitiesFromDateToDate<ItemsExtended>(
              itemsExtended,
              startDateYear,
              endDateYear,
              'createdAt'
            );

            const isBrokerAgreement = (a: Agreements) =>
              a.buyerUserId === selectedUserId ||
              a.sellerUserId === selectedUserId;

            const isOfficeAgreement = (a: Agreements) =>
              a.buyerOfficeId === selectedOffice ||
              a.sellerOfficeId === selectedOffice;

            const res = {
              user: {
                statsText: [
                  ...this.getInvoicesStat(
                    invoicesMonth
                      .filter((i) => i.brokerId === selectedUserId)
                      .filter(
                        (i) =>
                          i.agreement?.commodityType === selectedCommodity ||
                          !selectedCommodity
                      ),
                    invoicesYear
                      .filter((i) => i.brokerId === selectedUserId)
                      .filter(
                        (i) =>
                          i.agreement?.commodityType === selectedCommodity ||
                          !selectedCommodity
                      ),
                    currenciesConversions
                  ),
                  ...this.getAgreementStat(
                    'approved',
                    agreementsMonth.filter(
                      (a) =>
                        isBrokerAgreement(a) &&
                        a.contractStatus === 'approvedByParties' &&
                        (a.commodityType === selectedCommodity ||
                          !selectedCommodity)
                    ),
                    agreementsYear.filter(
                      (a) =>
                        isBrokerAgreement(a) &&
                        a.contractStatus === 'approvedByParties' &&
                        (a.commodityType === selectedCommodity ||
                          !selectedCommodity)
                    ),
                    currenciesConversions
                  ),
                  ...this.getAgreementStat(
                    'pending',
                    agreementsMonth.filter(
                      (a) =>
                        isBrokerAgreement(a) &&
                        pendingStatus.includes(a.contractStatus as any) &&
                        (a.commodityType === selectedCommodity ||
                          !selectedCommodity)
                    ),
                    agreementsYear.filter(
                      (a) =>
                        isBrokerAgreement(a) &&
                        pendingStatus.includes(a.contractStatus as any) &&
                        (a.commodityType === selectedCommodity ||
                          !selectedCommodity)
                    ),
                    currenciesConversions
                  ),
                  ...this.getItemStat(
                    itemsMonth.filter(
                      (i) =>
                        i.brokerId === selectedUserId &&
                        (i.commodityType === selectedCommodity ||
                          !selectedCommodity)
                    ),
                    itemsYear.filter(
                      (i) =>
                        i.brokerId === selectedUserId &&
                        (i.commodityType === selectedCommodity ||
                          !selectedCommodity)
                    ),
                    currenciesConversions
                  ),
                ],
              },
              office: {
                statsText: [
                  ...this.getInvoicesStat(
                    invoicesMonth
                      .filter((i) => i.officeId === selectedOffice)
                      .filter(
                        (i) =>
                          i.agreement?.commodityType === selectedCommodity ||
                          !selectedCommodity
                      ),
                    invoicesYear
                      .filter((i) => i.officeId === selectedOffice)
                      .filter(
                        (i) =>
                          i.agreement?.commodityType === selectedCommodity ||
                          !selectedCommodity
                      ),
                    currenciesConversions
                  ),
                  ...this.getAgreementStat(
                    'approved',
                    agreementsMonth.filter(
                      (a) =>
                        isOfficeAgreement(a) &&
                        a.contractStatus === 'approvedByParties' &&
                        (a.commodityType === selectedCommodity ||
                          !selectedCommodity)
                    ),
                    agreementsYear.filter(
                      (a) =>
                        isOfficeAgreement(a) &&
                        a.contractStatus === 'approvedByParties' &&
                        (a.commodityType === selectedCommodity ||
                          !selectedCommodity)
                    ),
                    currenciesConversions
                  ),
                  ...this.getAgreementStat(
                    'pending',
                    agreementsMonth.filter(
                      (a) =>
                        isOfficeAgreement(a) &&
                        pendingStatus.includes(a.contractStatus as any) &&
                        (a.commodityType === selectedCommodity ||
                          !selectedCommodity)
                    ),
                    agreementsYear.filter(
                      (a) =>
                        isOfficeAgreement(a) &&
                        pendingStatus.includes(a.contractStatus as any) &&
                        (a.commodityType === selectedCommodity ||
                          !selectedCommodity)
                    ),
                    currenciesConversions
                  ),
                  ...this.getItemStat(
                    itemsMonth.filter(
                      (i) =>
                        i.officeId === selectedOffice &&
                        (i.commodityType === selectedCommodity ||
                          !selectedCommodity)
                    ),
                    itemsYear.filter(
                      (i) =>
                        i.officeId === selectedOffice &&
                        (i.commodityType === selectedCommodity ||
                          !selectedCommodity)
                    ),
                    currenciesConversions
                  ),
                ],
              },
            };

            return res;
          });
        }
      ),
      shareReplay(1)
    );

    this.earningsBuySpecs$ = combineLatest([
      itemExtended$,
      this.storeService.store.agreements.data$,
      this.storeService.store.buySpecs.data$,
      this.storeService.store.samples.data$,
      this.storeService.getStoreByKeys$<SampleShipments>('sampleShipments'),
      this.selectedOfficeForSnapshot.valueChanges.pipe(
        startWith(this.selectedOfficeForSnapshot.value)
      ),
      this.commodityForSnapshot.valueChanges.pipe(
        startWith(this.commodityForSnapshot.value),
        map((value) => (value === 'all' ? null : value))
      ),
      this.storeService.store.currenciesConversions.data$,
    ]).pipe(
      map(
        ([
          itemsExtended,
          agreements,
          buySpecs,
          samples,
          sampleShipmentsByKeys,
          selectedOfficeForSnapshot,
          commodityForSnapshot,
          currenciesConversions,
        ]) => {
          return this.zone.runOutsideAngular(() => {
            const startDateYear = this.yearDates[0];
            const endDateYear = this.yearDates[1];

            const itemsYear = this.getEntitiesFromDateToDate<ItemsExtended>(
              itemsExtended,
              startDateYear,
              endDateYear,
              'createdAt'
            );

            const agreementsGroupByBuySpecsId = groupByMultiple(
              agreements,
              'buyerSpecsId'
            );

            buySpecs = buySpecs.map((data: BuySpecs) => ({
              ...data,
              agreementsIds: agreementsGroupByBuySpecsId[data.id]?.map(
                (agreement) => agreement.id
              ),
            }));

            const yearBuySpecs = this.getEntitiesFromDateToDate<BuySpecs>(
              buySpecs,
              startDateYear,
              endDateYear,
              'createdAt'
            )
              .filter((i) => i.officeId === selectedOfficeForSnapshot)
              .filter(
                (i) =>
                  i.commodityType === commodityForSnapshot ||
                  !commodityForSnapshot
              );

            const winBuySpecs: BuySpecs[] = yearBuySpecs.filter(
              (bs) => bs?.agreementsIds?.length
            );

            const openBuySpecs: BuySpecs[] = yearBuySpecs.filter(
              (bs) => !bs?.agreementsIds?.length && !bs.archivedAt
            );

            const lostBuySpecs: BuySpecs[] = yearBuySpecs.filter((bs) =>
              isAgreementLost(bs)
            );

            const officeItemsYear = itemsYear
              .filter((i) => i.officeId === selectedOfficeForSnapshot)
              .filter(
                (i) =>
                  !commodityForSnapshot ||
                  i.commodityType === commodityForSnapshot
              );

            const openItems = officeItemsYear.filter((i) => !i.archivedAt);

            const lostItem = officeItemsYear.filter(
              (i) => i.archivedAt && !i.agreementsIds?.length
            );

            const wonItems = officeItemsYear.filter(
              (i) => i.archivedAt && i.agreementsIds?.length
            );

            let samplesYear: Samples[] =
              this.getEntitiesFromDateToDate<Samples>(
                samples,
                startDateYear,
                endDateYear,
                'createdAt'
              )
                .filter((i) => i.officeId === selectedOfficeForSnapshot)
                .filter(
                  (i) =>
                    i.commodityType === commodityForSnapshot ||
                    !commodityForSnapshot
                );

            samplesYear = copy(samplesYear) as Samples[];
            samplesYear = samplesYear.map((s) =>
              this.storeService.jointObject<Samples>(
                s,
                'sampleShipmentId',
                'sampleShipment',
                sampleShipmentsByKeys
              )
            );

            const shippedSamples = samplesYear.filter((sample) => {
              return sample?.sampleShipment?.status === 'delivered';
            });

            const inSampleRoomSample = samplesYear.filter((sample) => {
              return !sample.sampleShipmentId && !sample.archivedAt;
            });

            // TODO wait for sample replenishment data
            const toReplenishSample = samplesYear.filter((sample) => {
              return !sample.sampleShipmentId && !sample.archivedAt;
            });

            const waitingForFeedback = samplesYear.filter((sample) => {
              return sample;
            });

            return [
              {
                title: 'BuySpecs',
                stats: [
                  {
                    title: 'Open (Comm. Amount)',
                    sum: this.getTextFromGroupByCurrency(
                      this.processedInvoiceGroupByMonth(
                        openBuySpecs,
                        (i) => i.commissionAmount,
                        (i) => i.currency
                      ),
                      currenciesConversions
                    ),

                    icon: 'box-3d-67',
                  },
                  {
                    title: 'Won (Comm. Amount)',
                    sum: this.getTextFromGroupByCurrency(
                      this.processedInvoiceGroupByMonth(
                        winBuySpecs,
                        (i) => i.commissionAmount,
                        (i) => i.currency
                      ),
                      currenciesConversions
                    ),

                    icon: 'networking',
                  },
                  {
                    title: 'Lost (Comm. Amount)',
                    sum: this.getTextFromGroupByCurrency(
                      this.processedInvoiceGroupByMonth(
                        lostBuySpecs,
                        (i) => i.commissionAmount,
                        (i) => i.currency
                      ),
                      currenciesConversions
                    ),

                    icon: 'empty',
                  },

                  {
                    title: 'Open (Qty needed)',
                    sum: this.getTextFromGroupBy(
                      this.processedInvoiceGroupByMonth(
                        openBuySpecs,
                        (i) => i.quantityNeeded,
                        (i) => getUom(i.uom)
                      )
                    ),

                    icon: 'box-3d-67',
                  },
                  {
                    title: 'Won (Qty needed)',
                    sum: this.getTextFromGroupBy(
                      this.processedInvoiceGroupByMonth(
                        winBuySpecs,
                        (i) => i.quantityNeeded,
                        (i) => getUom(i.uom)
                      )
                    ),

                    icon: 'networking',
                  },
                  {
                    title: 'Lost (Qty needed)',
                    sum: this.getTextFromGroupBy(
                      this.processedInvoiceGroupByMonth(
                        lostBuySpecs,
                        (i) => i.quantityNeeded,
                        (i) => getUom(i.uom)
                      )
                    ),

                    icon: 'empty',
                  },
                ],
              },

              {
                title: 'Items',
                stats: [
                  {
                    title: 'Open (count)',
                    sum: `${openItems?.length}`,

                    icon: 'receipt-1',
                  },
                  {
                    title: 'Won (count)',
                    sum: `${wonItems?.length}`,

                    icon: 'networking',
                  },
                  {
                    title: 'Lost (count)',
                    sum: `${lostItem?.length}`,

                    icon: 'empty',
                  },

                  /*
              {
                title: 'Open (Qty in sale)',
                sum: this.getTextFromInvoiceGroupByCurrency(
                  this.processedInvoiceGroupByMonth(
                    openBuySpecs,
                    (i) => i.quantityNeeded,
                    (i) => getUom(i.uom)
                  )
                ),

                icon: 'receipt-1',
              },
              {
                title: 'Won (Qty in sale)',
                sum: this.getTextFromInvoiceGroupByCurrency(
                  this.processedInvoiceGroupByMonth(
                    winBuySpecs,
                    (i) => i.quantityNeeded,
                    (i) => getUom(i.uom)
                  )
                ),

                icon: 'receipt-1',
              },
              {
                title: 'Lost (Qty in sale)',
                sum: this.getTextFromInvoiceGroupByCurrency(
                  this.processedInvoiceGroupByMonth(
                    lostBuySpecs,
                    (i) => i.quantityNeeded,
                    (i) => getUom(i.uom)
                  )
                ),

                icon: 'receipt-1',
              },
              */
                ],
              },

              {
                title: 'Samples',
                stats: [
                  {
                    title: 'Shipped',
                    sum: `${shippedSamples?.length}`,

                    icon: 'delivery-time',
                  },
                  {
                    title: 'In the Sample Room',
                    sum: `${inSampleRoomSample?.length}`,

                    icon: 'storage-unit',
                  },
                ],
              },
            ];
          });
        }
      )
    );
  }

  ngOnDestroy(): void {
    //Called once, before the instance is destroyed.
    //Add 'implements OnDestroy' to the class.
    this._destroy$.next(true);
    this._destroy$.complete();
  }

  private _getDatesPeriod(type: 'month' | 'year'): string[] {
    const date = new Date();

    if (type === 'month') {
      const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
      const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);

      return [firstDay.toISOString(), lastDay.toISOString()];
    } else if (type === 'year') {
      const firstDay = new Date(date.getFullYear(), 0, 1);
      const lastDay = new Date(date.getFullYear(), 11, 31);

      return [firstDay.toISOString(), lastDay.toISOString()];
    }

    return [];
  }

  private getTextFromGroupByCurrency(
    invoiceGroupByCurrency: InvoiceGroupByCurrency[],
    currenciesConversions: CurrenciesConversions[]
  ) {
    if (!invoiceGroupByCurrency.length) {
      return 'No data';
    }

    return formatCurrency(
      sum(
        invoiceGroupByCurrency
          .map((i) =>
            convertCurrencyToUSD(
              currenciesConversions,
              new Date().toISOString(),
              i.currency,
              i.sum
            )
          )
          .filter((i) => i)
      ),
      undefined,
      'USD',
      '1.0-0'
    );
  }

  private getTextFromGroupBy(invoiceGroupByCurrency: InvoiceGroupByCurrency[]) {
    if (!invoiceGroupByCurrency.length) {
      return 'No data';
    }

    return invoiceGroupByCurrency
      .map((i) => `${i.currency}: ${formatNumber(i.sum)}`)
      .join(', ');
  }

  private processedInvoiceGroupByMonth<T>(
    entities: T[],
    getValue: (invoice: T) => any,
    getField: (invoice: T) => any,
    debug = false
  ): InvoiceGroupByCurrency[] {
    const invoiceGroupByMonth = groupBy<T>(entities, (i) => getField(i));
    if (debug) {
      console.log(
        '🔥processedInvoiceGroupByMonth',
        entities,
        invoiceGroupByMonth
      );
    }

    return Object.keys(invoiceGroupByMonth).map((currency) => {
      const sum2 = invoiceGroupByMonth[currency]
        .map((i) => getValue(i))
        .filter((i) => i);

      return {
        currency,
        count: invoiceGroupByMonth[currency].length,
        sum: sum(sum2),
      };
    });
  }

  public getInvoicesStat(
    invoicesMonth: Invoices[],
    invoicesYear: Invoices[],
    currenciesConversions: CurrenciesConversions[]
  ): StatsSection[] {
    return [
      {
        title: 'Item fulfillments',
        stats: [
          {
            title: 'Invoiced this Month (MTD)',
            sum: this.getTextFromGroupByCurrency(
              this.processedInvoiceGroupByMonth(
                invoicesMonth,
                (i) => i.amount,
                (i) => i.currency
              ),
              currenciesConversions
            ),

            icon: 'receipt-1',
          },
          {
            title: 'Invoiced this Year (YTD)',
            sum: this.getTextFromGroupByCurrency(
              this.processedInvoiceGroupByMonth(
                invoicesYear,
                (i) => i.amount,
                (i) => i.currency,
                true
              ),
              currenciesConversions
            ),

            icon: 'currency-dollar',
          },
          {
            title: 'Quantity fulfilled this Month (MTD)',
            sum: this.getTextFromGroupBy(
              this.processedInvoiceGroupByMonth(
                invoicesMonth.filter(
                  (i) => i?.shipmentFulfillment?.status !== 'cancelled'
                ),
                (i) => i.shipmentFulfillment?.quantity,
                (i) => getUom(i?.shipmentFulfillment?.uom)
              )
            ),

            icon: 'app-1',
          },
          {
            title: 'Quantity fulfilled this Year (YTD)',
            sum: this.getTextFromGroupBy(
              this.processedInvoiceGroupByMonth(
                invoicesYear.filter(
                  (i) => i?.shipmentFulfillment?.status !== 'cancelled'
                ),
                (i) => i.shipmentFulfillment?.quantity,
                (i) => getUom(i?.shipmentFulfillment?.uom)
              )
            ),

            icon: 'blockchain',
          },
        ],
      },
    ];
  }

  public getItemStat(
    invoicesMonth: Items[],
    invoicesYear: Items[],
    currenciesConversions: CurrenciesConversions[]
  ): StatsSection[] {
    return [
      {
        title: 'Potential Deals',
        stats: [
          {
            title: 'New Items Potential Earnings (MTD)',
            sum: this.getTextFromGroupByCurrency(
              this.processedInvoiceGroupByMonth(
                invoicesMonth,
                (i) => this.itemsService.getEstimatedCommission(i),
                (i) => i.currency
              ),
              currenciesConversions
            ),

            icon: 'receipt-1',
          },
          {
            title: 'New Items Potential Earnings (YTD)',
            sum: this.getTextFromGroupByCurrency(
              this.processedInvoiceGroupByMonth(
                invoicesYear,
                (i) => this.itemsService.getEstimatedCommission(i),
                (i) => i.currency
              ),
              currenciesConversions
            ),

            icon: 'currency-dollar',
          },
          {
            title: 'New Items Quantity (MTD)',
            sum: this.getTextFromGroupBy(
              this.processedInvoiceGroupByMonth(
                invoicesMonth,
                (i) => i?.quantity,
                (i) => getUom(i?.uom)
              )
            ),

            icon: 'app-1',
          },
          {
            title: 'New Items Quantity (YTD)',
            sum: this.getTextFromGroupBy(
              this.processedInvoiceGroupByMonth(
                invoicesYear,
                (i) => i?.quantity,
                (i) => getUom(i?.uom)
              )
            ),

            icon: 'blockchain',
          },
        ],
      },
    ];
  }

  public getAgreementStat(
    type: 'approved' | 'pending',
    agreementsMonth: Agreements[],
    agreementsAgreementsYear: Agreements[],
    currenciesConversions: CurrenciesConversions[]
  ): StatsSection[] {
    const globalCostArrayMonth = flatten(
      agreementsMonth.map((a) => a.costArray)
    );

    const globalCostArrayYear = flatten(
      agreementsAgreementsYear.map((a) => a.costArray)
    );

    const texts: Record<'approved' | 'pending', any> = {
      approved: {
        title: 'Approved S&P',
        sum: 'Approved S&P Earnings',
        quantity: 'Approved S&P Quantity',
      },
      pending: {
        title: 'Pending S&P',
        sum: 'Pending S&P Earnings',
        quantity: 'Pending S&P Quantity',
      },
    };

    const getText = (field: string) => {
      return texts[type][field];
    };

    return [
      {
        title: getText('title'),
        stats: [
          {
            title: `${getText('sum')} This Month`,
            sum: this.getTextFromGroupByCurrency(
              this.processedInvoiceGroupByMonth(
                agreementsMonth,
                (i) => this.agreementsService.getCommissionFromAgreement(i),
                (i) => i.commissionCurrency
              ),
              currenciesConversions
            ),

            icon: 'receipt-1',
          },
          {
            title: `${getText('sum')} This Year`,
            sum: this.getTextFromGroupByCurrency(
              this.processedInvoiceGroupByMonth(
                agreementsAgreementsYear,
                (i) => this.agreementsService.getCommissionFromAgreement(i),
                (i) => i.commissionCurrency
              ),
              currenciesConversions
            ),

            icon: 'currency-dollar',
          },
          {
            title: `${getText('quantity')} This Month`,
            sum: this.getTextFromGroupBy(
              this.processedInvoiceGroupByMonth(
                globalCostArrayMonth,
                (i) => i.quantity,
                (i) => getUom(i.uom)
              )
            ),

            icon: 'app-1',
          },
          {
            title: `${getText('quantity')} This Year`,
            sum: this.getTextFromGroupBy(
              this.processedInvoiceGroupByMonth(
                globalCostArrayYear,
                (i) => i.quantity,
                (i) => getUom(i.uom)
              )
            ),

            icon: 'blockchain',
          },
        ],
      },
    ];
  }

  private getEntitiesFromDateToDate<T>(
    entities: T[],
    startDate: string,
    endDate: string,
    dateKey: keyof T
  ): T[] {
    return entities.filter(
      (i: any) => i[dateKey] >= startDate && i[dateKey] <= endDate
    );
  }

  isSameMonthAndYear(date1: Date, date2: Date): boolean {
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth()
    );
  }
}
